import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { DotsThreeVertical, Trash, CircleNotch } from '@phosphor-icons/react';
import { getCategoryColor, getCategoryLabel } from 'config/ideaCategories';
import { deleteIdea } from 'api/companyMapping';
import { useFeedback } from 'contexts/feedback';

export default function CompanyMappingIdeasListItem({ idea, onViewDetail }) {
    const { conversationId } = useParams();
    const [openMenu, setOpenMenu] = useState(false);

    const { setSuccess, setError } = useFeedback();
    const queryClient = useQueryClient();

    const { mutate: deleteIdeaMutate, status: deleteStatus } = useMutation({
        mutationKey: ['deleteCompanyMappingIdea', idea._id || idea.id],
        mutationFn: deleteIdea,
        onSuccess: () => {
            queryClient.invalidateQueries(['company-mapping-ideas', conversationId]);
            setSuccess('Idea eliminata');
        },
        onError: () => {
            setError('Errore durante l\'eliminazione');
        }
    });

    const color = getCategoryColor(idea.category);
    const ease = idea.ease || (10 - idea.difficulty + 1);
    const isDeleting = deleteStatus === 'pending';

    const handleMenuToggle = (e) => {
        e.stopPropagation();
        setOpenMenu(!openMenu);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        setOpenMenu(false);
        deleteIdeaMutate({ id: idea._id || idea.id });
    };

    const handleRowClick = () => {
        if (!isDeleting) {
            onViewDetail?.(idea);
        }
    };

    return (
        <tr
            className={`border-b border-black/5 hover:bg-black/[0.02] transition-colors cursor-pointer ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={handleRowClick}
        >
            <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                    <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                    />
                    <span className="font-medium text-black/90">
                        {idea.title}
                    </span>
                </div>
            </td>
            <td className="py-4 px-4">
                <span className="text-black/60 text-sm">
                    {getCategoryLabel(idea.category)}
                </span>
            </td>
            <td className="py-4 px-4 text-center">
                <span className="text-black/80 font-medium">
                    {idea.impact}
                </span>
            </td>
            <td className="py-4 px-4 text-center">
                <span className="text-black/80 font-medium">
                    {ease}
                </span>
            </td>
            <td className="py-4 px-4">
                <div className="flex justify-center relative">
                    <button
                        onClick={handleMenuToggle}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors"
                        disabled={isDeleting}
                    >
                        {isDeleting ? (
                            <CircleNotch size={20} className="text-black/50 animate-spin" />
                        ) : (
                            <DotsThreeVertical size={20} className="text-black/50" weight="bold" />
                        )}
                    </button>

                    {openMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(false);
                                }}
                            />
                            <div className="absolute right-0 top-full mt-1 bg-white border border-black/10 rounded-xl shadow-lg z-20 py-1 min-w-[140px]">
                                <button
                                    onClick={handleDelete}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50 transition-colors text-red-600"
                                >
                                    <Trash size={18} />
                                    <span className="text-sm">Elimina</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}
